/*
 * Neuraxial and deep block timing for antithrombotic drugs, from the ASRA Pain Medicine
 * evidence-based guidelines, fifth edition (Kopp et al., Reg Anesth Pain Med, January 2025).
 * Structure and much of the wording follow Dr Tony Kim's ASRA Anticoag lookup
 * (github.com/tguestkim-cmd/asra-anticoag-app); values were re-checked against the
 * fifth edition, which changed several intervals from the 2018 fourth edition.
 *
 *   drug    = { id, name, aliases, cls, variants?: [{ id, label }], rec | byVariant }
 *   rec     = { hold, catheter, restart, labs, notes, grade }
 *   field   = { text, tone?: 'safe' | 'warn' | 'danger' }
 *
 * Grades are the guideline's: I/II = strong/weak recommendation, A/B/C = evidence quality.
 */

export const ASRA_EDITION = 'ASRA fifth edition, 2025';

export const ASRA_SOURCES = [
  {
    title: 'ASRA guidelines, fifth edition',
    detail: 'Regional Anesthesia and Pain Medicine, 2025',
    href: 'https://rapm.bmj.com/lookup/doi/10.1136/rapm-2024-105766',
  },
];

export const ASRA_CREDIT = 'Adapted from the ASRA Anticoag Lookup App by Dr. Tony Kim.';

/*
 * Block type. The fifth edition applies the neuraxial intervals to deep plexus and deep
 * peripheral blocks (IC). For superficial, compressible sites it sets no mandatory
 * interval: timing rests on compressibility, vascularity and the consequences of a
 * bleed (IIC). Which blocks count as deep is not tabulated in the guideline; the
 * examples below follow common practice and need local confirmation.
 */
export const BLOCK_TYPES = [
  { id: 'neuraxial', label: 'Neuraxial & deep' },
  { id: 'superficial', label: 'Superficial' },
];

export const DEEP_BLOCKS = ['Lumbar plexus / psoas compartment', 'Paravertebral', 'Proximal sciatic (parasacral, subgluteal)', 'Infraclavicular', 'Deep cervical plexus', 'Celiac plexus', 'Stellate ganglion'];
export const SUPERFICIAL_BLOCKS = ['Interscalene', 'Supraclavicular', 'Axillary', 'Femoral', 'Adductor canal', 'Popliteal sciatic', 'Ankle', 'TAP and rectus sheath', 'PECS and serratus', 'Superficial cervical plexus'];

/** What every drug card shows for a superficial, compressible site: the guideline sets no interval */
export const SUPERFICIAL_REC = {
  hold: { text: 'No specified interval' },
  catheter: { text: 'No specified interval' },
  restart: { text: 'No specified interval' },
  labs: { text: 'None specified' },
  notes: { text: 'Judge by site compressibility, vascularity and the consequences of a bleed. Deep plexus and deep peripheral blocks follow the neuraxial intervals instead.' },
  grade: 'IIC',
};

/* Drug classes, as the page's segments. Order within a class is the chip order. */
export const ASRA_CLASSES = [
  { id: 'antiplatelet', label: 'Antiplatelet', drugs: ['aspirin', 'clopidogrel', 'prasugrel', 'ticagrelor', 'cangrelor', 'cilostazol', 'gp2b3a'] },
  { id: 'heparin', label: 'Heparins', drugs: ['ufh', 'lmwh', 'fondaparinux'] },
  { id: 'doac', label: 'DOACs', drugs: ['apixaban', 'rivaroxaban', 'edoxaban', 'dabigatran'] },
  { id: 'warfarin', label: 'Warfarin', drugs: ['warfarin'] },
  { id: 'other', label: 'Other', drugs: ['dti', 'thrombolytics'] },
  { id: 'herbal', label: 'Herbals', drugs: ['herbal'] },
];

/* Short names for the chips; the card keeps the full name */
export const ASRA_SHORT = { aspirin: 'Aspirin/NSAIDs', gp2b3a: 'GP IIb/IIIa', ufh: 'Heparin', dti: 'Argatroban/bivalirudin', herbal: 'Herbals' };

const safe = (text) => ({ text, tone: 'safe' });
const warn = (text) => ({ text, tone: 'warn' });
const danger = (text) => ({ text, tone: 'danger' });
const plain = (text) => ({ text });

const DOAC_LAB = (drug, hours) => plain(`If under ${hours} h since the last dose: ${drug}-calibrated anti-Xa or drug level. Proceed only if the level is under 30 ng/mL or anti-Xa activity is 0.1 IU/mL or less. A heparin-calibrated anti-Xa does not count.`);

export const ASRA_DRUGS = [
  {
    id: 'aspirin',
    name: 'Aspirin and NSAIDs',
    aliases: ['aspirin', 'asa', 'nsaid', 'nsaids', 'ibuprofen', 'naproxen', 'ketorolac', 'toradol', 'celecoxib', 'cox'],
    cls: 'Antiplatelet, COX inhibitor',
    rec: {
      hold: safe('No hold'),
      catheter: safe('No restriction'),
      restart: safe('No restriction'),
      labs: plain('None'),
      notes: plain('On their own, NSAIDs including aspirin do not add meaningful spinal hematoma risk. The risk rises when they are combined with heparin, LMWH, warfarin or a DOAC early after surgery; avoid that combination with a catheter in place.'),
      grade: 'IC',
    },
  },
  {
    id: 'clopidogrel',
    name: 'Clopidogrel',
    aliases: ['clopidogrel', 'plavix', 'p2y12', 'thienopyridine'],
    cls: 'Antiplatelet, P2Y12 inhibitor',
    rec: {
      hold: warn('5–7 days before neuraxial or deep block'),
      catheter: warn('May stay in for 1–2 days on a maintenance dose with no loading dose. Not with a loading dose'),
      restart: plain('Right after catheter removal without a loading dose. 6 h after removal if a loading dose is given'),
      labs: plain('None routinely. A platelet function assay is optional'),
      notes: plain('Irreversible. Risk is substantially higher when combined with aspirin or an anticoagulant.'),
      grade: 'IIC',
    },
  },
  {
    id: 'prasugrel',
    name: 'Prasugrel',
    aliases: ['prasugrel', 'effient'],
    cls: 'Antiplatelet, P2Y12 inhibitor',
    rec: {
      hold: warn('7–10 days before neuraxial or deep block'),
      catheter: danger('Do not keep a catheter in on prasugrel'),
      restart: plain('Right after catheter removal without a loading dose. 6 h after removal with a loading dose'),
      labs: plain('None routinely'),
      notes: plain('Faster onset and more potent than clopidogrel, with more bleeding.'),
      grade: 'IIC',
    },
  },
  {
    id: 'ticagrelor',
    name: 'Ticagrelor',
    aliases: ['ticagrelor', 'brilinta', 'brilique'],
    cls: 'Antiplatelet, P2Y12 inhibitor',
    rec: {
      hold: warn('5 days before neuraxial or deep block'),
      catheter: danger('Do not keep a catheter in on ticagrelor'),
      restart: plain('Right after catheter removal without a loading dose. 6 h after removal with a loading dose'),
      labs: plain('None routinely'),
      notes: plain('Reversible, but the fifth edition still advises against catheters. Usually paired with aspirin, which adds to the risk.'),
      grade: 'IIC',
    },
  },
  {
    id: 'cangrelor',
    name: 'Cangrelor',
    aliases: ['cangrelor', 'kengreal'],
    cls: 'Antiplatelet, IV P2Y12 inhibitor',
    rec: {
      hold: warn('3 h after the infusion stops'),
      catheter: warn('Remove the catheter before restarting'),
      restart: plain('8 h after catheter removal'),
      labs: plain('None routinely'),
      notes: plain('Active metabolite half-life of minutes; platelet function returns within about an hour. Little neuraxial data.'),
      grade: 'IIC',
    },
  },
  {
    id: 'cilostazol',
    name: 'Cilostazol',
    aliases: ['cilostazol', 'pletal', 'phosphodiesterase'],
    cls: 'Antiplatelet, phosphodiesterase inhibitor',
    rec: {
      hold: warn('2 days before neuraxial or deep block'),
      catheter: warn('Remove the catheter before restarting'),
      restart: plain('6 h after catheter removal'),
      labs: plain('None routinely'),
      notes: plain('Used for claudication. New to the fifth edition.'),
      grade: 'IIC',
    },
  },
  {
    id: 'gp2b3a',
    name: 'GP IIb/IIIa inhibitors',
    aliases: ['abciximab', 'reopro', 'eptifibatide', 'integrilin', 'tirofiban', 'aggrastat', 'gp iib iiia', 'glycoprotein'],
    cls: 'Antiplatelet, GP IIb/IIIa inhibitor',
    rec: {
      hold: danger('Until platelet function has recovered: 24–48 h for abciximab, 4–8 h for eptifibatide and tirofiban'),
      catheter: danger('Avoid neuraxial techniques until platelet function is back'),
      restart: plain('Given emergently with a catheter in: neurological monitoring, and an infusion that spares motor and sensory function'),
      labs: plain('Platelet function assay to confirm recovery if timing is tight'),
      notes: plain('Profound platelet inhibition. Abciximab lasts far longer than the small-molecule agents.'),
      grade: 'IIC',
    },
  },
  {
    id: 'ufh',
    name: 'Unfractionated heparin',
    aliases: ['heparin', 'ufh', 'unfractionated', 'iv heparin', 'sc heparin', 'sq heparin', 'subcutaneous heparin', 'heparin drip'],
    cls: 'Anticoagulant',
    variants: [
      { id: 'iv', label: 'IV infusion' },
      { id: 'sc-low', label: 'SC 5,000 U BID or TID' },
      { id: 'sc-high', label: 'SC 7,500–10,000 U' },
      { id: 'sc-vhigh', label: 'SC over 10,000 U' },
    ],
    byVariant: {
      iv: {
        hold: warn('Stop the infusion 4–6 h before and confirm normal coagulation'),
        catheter: danger('No neuraxial or deep catheter during a continuous heparin infusion. Remove once coagulation is normal'),
        restart: plain('At least 1 h after needle placement'),
        labs: plain('aPTT before the block or removal. Platelet count if on heparin over 4 days'),
        notes: plain('Unplanned heparinisation with a catheter in: postoperative neurological checks and a dilute local anesthetic so a hematoma shows early.'),
        grade: 'IA',
      },
      'sc-low': {
        hold: warn('4–6 h after the last dose, or confirm normal coagulation'),
        catheter: warn('A catheter may stay in. Remove it 4–6 h after a dose'),
        restart: safe('Right after the block or catheter removal'),
        labs: plain('Platelet count if on heparin over 4 days'),
        notes: plain('Prophylactic dosing. Avoid adding an antiplatelet agent with a catheter in place.'),
        grade: 'IIC',
      },
      'sc-high': {
        hold: warn('12 h after the last dose and normal coagulation confirmed'),
        catheter: warn('Postoperative doses over 5,000 U or over 15,000 U a day: catheter safety not established. Weigh risk and benefit per patient'),
        restart: plain('Right after catheter removal'),
        labs: plain('aPTT. Platelet count if over 4 days'),
        notes: plain('7,500–10,000 U BID, or up to 20,000 U a day. No concurrent antiplatelet or anticoagulant.'),
        grade: 'IIC',
      },
      'sc-vhigh': {
        hold: danger('24 h after the last dose and normal coagulation confirmed'),
        catheter: danger('Treat as a therapeutic dose. Catheter not recommended'),
        restart: plain('Per the team, after catheter removal'),
        labs: plain('aPTT. Platelet count. Anti-Xa if indicated'),
        notes: plain('Over 10,000 U per dose or over 20,000 U a day.'),
        grade: 'IIC',
      },
    },
  },
  {
    id: 'lmwh',
    name: 'LMWH',
    aliases: ['lmwh', 'enoxaparin', 'lovenox', 'dalteparin', 'fragmin', 'tinzaparin', 'low molecular weight heparin'],
    cls: 'Anticoagulant, low molecular weight heparin',
    variants: [
      { id: 'low-qd', label: 'Low dose, once daily' },
      { id: 'low-bid', label: 'Low dose, twice daily' },
      { id: 'high', label: 'High dose' },
    ],
    byVariant: {
      'low-qd': {
        hold: warn('12 h before needle placement or catheter removal'),
        catheter: warn('May stay in. First dose 12 h or more after placement, second dose no sooner than 24 h after the first. Remove 12 h after the last dose'),
        restart: safe('4 h after catheter removal'),
        labs: plain('Anti-Xa 0.1 IU/mL or less if under 12 h since the last dose. Platelet count if on LMWH over 4 days'),
        notes: plain('For example enoxaparin 40 mg daily. No antiplatelet drug or heparin alongside a catheter.'),
        grade: 'IC',
      },
      'low-bid': {
        hold: warn('12 h before needle placement or catheter removal'),
        catheter: warn('Remove the catheter before the first dose. First dose the day after surgery and at least 12 h after placement'),
        restart: safe('4 h after catheter removal'),
        labs: plain('Anti-Xa 0.1 IU/mL or less if under 12 h since the last dose. Platelet count if over 4 days'),
        notes: plain('For example enoxaparin 30 mg BID. Twice-daily dosing carries more hematoma risk than once daily.'),
        grade: 'IC',
      },
      high: {
        hold: danger('24 h before needle placement or catheter removal'),
        catheter: danger('Remove the catheter 4 h before the first postoperative dose'),
        restart: plain('First dose 24 h after surgery, 48–72 h after high-bleeding-risk surgery, and at least 24 h after needle placement'),
        labs: plain('Anti-Xa 0.1 IU/mL or less if under 24 h since the last dose, especially over 75 years or CrCl 30 mL/min or less'),
        notes: plain('For example enoxaparin 1 mg/kg BID or 1.5 mg/kg daily. A bloody or difficult placement: delay the first dose 24 h and tell the surgeon.'),
        grade: 'IC',
      },
    },
  },
  {
    id: 'warfarin',
    name: 'Warfarin',
    aliases: ['warfarin', 'coumadin', 'jantoven', 'vka', 'vitamin k antagonist'],
    cls: 'Anticoagulant, vitamin K antagonist',
    rec: {
      hold: warn('Stop 5 days before and confirm the INR is in the lab’s normal range'),
      catheter: warn('Remove when INR is under 1.5. INR 1.5–3: keep or remove with caution, following the INR daily. INR over 3: hold or reduce the dose'),
      restart: plain('Per the team after removal, weighing thrombotic risk'),
      labs: plain('INR before the block, and daily with a catheter in. Check it if the first dose was over 24 h ago or a second dose has been given'),
      notes: plain('Neurological checks for 48 h after catheter removal. Watch sensory and motor function every 6–8 h with a catheter in.'),
      grade: 'IB',
    },
  },
  {
    id: 'rivaroxaban',
    name: 'Rivaroxaban',
    aliases: ['rivaroxaban', 'xarelto', 'doac', 'xa inhibitor', 'factor xa'],
    cls: 'DOAC, factor Xa inhibitor',
    variants: [
      { id: 'low', label: 'Low dose, 10 mg daily or less' },
      { id: 'high', label: 'High dose, 15–20 mg daily' },
    ],
    byVariant: {
      low: {
        hold: warn('24 h before neuraxial or deep block. 30 h if CrCl under 30 mL/min'),
        catheter: warn('Remove 6 h before the first postoperative dose. Unplanned dose with a catheter in: wait 24 h (30 h if CrCl under 30) or check a level'),
        restart: safe('6 h after catheter removal'),
        labs: DOAC_LAB('rivaroxaban', 24),
        notes: plain('VTE prophylaxis dosing. Reversal agents are not recommended just to allow a neuraxial procedure.'),
        grade: 'IC',
      },
      high: {
        hold: danger('72 h before neuraxial or deep block'),
        catheter: danger('Remove 24 h before the first postoperative dose. Unplanned dose with a catheter in: wait 72 h or check a level'),
        restart: plain('6 h after catheter removal'),
        labs: DOAC_LAB('rivaroxaban', 72),
        notes: plain('Atrial fibrillation or VTE treatment dosing. Impaired renal function raises residual levels; check a level when unsure.'),
        grade: 'IIC',
      },
    },
  },
  {
    id: 'apixaban',
    name: 'Apixaban',
    aliases: ['apixaban', 'eliquis', 'doac', 'xa inhibitor', 'factor xa'],
    cls: 'DOAC, factor Xa inhibitor',
    variants: [
      { id: 'low', label: 'Low dose, 2.5 mg BID' },
      { id: 'high', label: 'High dose, 5–10 mg BID' },
    ],
    byVariant: {
      low: {
        hold: warn('36 h before neuraxial or deep block'),
        catheter: warn('Remove 6 h before the first postoperative dose. Unplanned dose with a catheter in: wait 36 h or check a level'),
        restart: safe('6 h after catheter removal'),
        labs: DOAC_LAB('apixaban', 36),
        notes: plain('The fifth edition lengthened this from 26–30 h. 2.5 mg BID is a treatment dose, not a low dose, in a patient with two of: age 80 or over, weight 60 kg or under, creatinine 1.5 mg/dL or over.'),
        grade: 'IIC',
      },
      high: {
        hold: danger('72 h before neuraxial or deep block'),
        catheter: danger('Remove 24 h before the first postoperative dose. Unplanned dose with a catheter in: wait 72 h or check a level'),
        restart: plain('6 h after catheter removal'),
        labs: DOAC_LAB('apixaban', 72),
        notes: plain('Atrial fibrillation or VTE treatment dosing. Renal or hepatic impairment prolongs the effect.'),
        grade: 'IIC',
      },
    },
  },
  {
    id: 'edoxaban',
    name: 'Edoxaban',
    aliases: ['edoxaban', 'savaysa', 'lixiana', 'doac', 'xa inhibitor', 'factor xa'],
    cls: 'DOAC, factor Xa inhibitor',
    variants: [
      { id: 'low', label: 'Low dose, 15–30 mg daily' },
      { id: 'high', label: 'High dose, 60 mg daily' },
    ],
    byVariant: {
      low: {
        hold: warn('24 h before neuraxial or deep block. 30 h if CrCl under 30 mL/min'),
        catheter: warn('Remove 6 h before the first postoperative dose. Unplanned dose with a catheter in: wait 24 h or check a level'),
        restart: safe('6 h after catheter removal'),
        labs: DOAC_LAB('edoxaban', 24),
        notes: plain('VTE prophylaxis dosing; the 30 mg dose is also used with CrCl 15–50 or low body weight.'),
        grade: 'IIC',
      },
      high: {
        hold: danger('72 h before neuraxial or deep block'),
        catheter: danger('Remove 24 h before the first postoperative dose. Unplanned dose with a catheter in: wait 72 h or check a level'),
        restart: plain('6 h after catheter removal'),
        labs: DOAC_LAB('edoxaban', 72),
        notes: plain('Atrial fibrillation or VTE treatment dosing. Reversal agents are not recommended just to allow a neuraxial procedure.'),
        grade: 'IIC',
      },
    },
  },
  {
    id: 'dabigatran',
    name: 'Dabigatran',
    aliases: ['dabigatran', 'pradaxa', 'doac', 'direct thrombin inhibitor', 'dti'],
    cls: 'DOAC, direct thrombin inhibitor',
    variants: [
      { id: 'low', label: 'Low dose' },
      { id: 'high-50', label: 'High dose, CrCl 50 or more' },
      { id: 'high-30', label: 'High dose, CrCl 30–49' },
      { id: 'ckd', label: 'CrCl under 30' },
    ],
    byVariant: {
      low: {
        hold: warn('48 h before neuraxial or deep block'),
        catheter: warn('Remove 6 h before the first postoperative dose. Unplanned dose with a catheter in: wait 48 h or check a level'),
        restart: safe('6 h after catheter removal'),
        labs: plain('If under 48 h since the last dose: dabigatran level, proceed only if under 30 ng/mL. dTT or ECT if a level is unavailable'),
        notes: plain('Over 80% renally cleared, so renal function drives every interval. Idarucizumab reverses it within minutes but is not recommended just to allow a block.'),
        grade: 'IIC',
      },
      'high-50': {
        hold: danger('72 h before neuraxial or deep block'),
        catheter: danger('Remove 24 h before the first postoperative dose. Unplanned dose with a catheter in: wait 72 h or check a level'),
        restart: plain('6 h after catheter removal'),
        labs: plain('If under 72 h since the last dose: dabigatran level, proceed only if under 30 ng/mL'),
        notes: plain('Atrial fibrillation or VTE treatment dosing with CrCl 50 mL/min or more.'),
        grade: 'IIC',
      },
      'high-30': {
        hold: danger('120 h, 5 days, before neuraxial or deep block'),
        catheter: danger('Remove 24 h before the first postoperative dose. Unplanned dose with a catheter in: wait 120 h or check a level'),
        restart: plain('6 h after catheter removal'),
        labs: plain('If under 120 h since the last dose: dabigatran level, proceed only if under 30 ng/mL'),
        notes: plain('Estimated CrCl tends to overstate function in these patients, and it can fall further around surgery.'),
        grade: 'IIC',
      },
      ckd: {
        hold: danger('Against neuraxial or deep block, unless a dabigatran level is under 30 ng/mL'),
        catheter: danger('Not applicable'),
        restart: plain('Discuss alternative anticoagulation with the team'),
        labs: plain('Dabigatran level. A block is reasonable only if it is under 30 ng/mL'),
        notes: plain('Accumulation is unpredictable below CrCl 30 mL/min at any dose.'),
        grade: 'IIC',
      },
    },
  },
  {
    id: 'fondaparinux',
    name: 'Fondaparinux',
    aliases: ['fondaparinux', 'arixtra', 'pentasaccharide'],
    cls: 'Anticoagulant, indirect factor Xa inhibitor',
    variants: [
      { id: 'low', label: 'Low dose, 2.5 mg daily' },
      { id: 'high', label: 'High dose, 5–10 mg daily' },
    ],
    byVariant: {
      low: {
        hold: danger('36 h in younger patients, 42 h in older patients, with normal renal function. 58 h if CrCl 30–50. Against a block if CrCl under 30'),
        catheter: danger('Remove the catheter 6 h before the first postoperative dose'),
        restart: plain('6 h after catheter removal'),
        labs: plain('Fondaparinux-calibrated anti-Xa 0.1 IU/mL or less if placing the needle earlier than these times'),
        notes: plain('Half-life 17–21 h, about 72 h in severe renal impairment. No reversal agent.'),
        grade: 'IIC',
      },
      high: {
        hold: danger('70 h in younger patients, 105 h in older patients, with normal renal function'),
        catheter: danger('Remove the catheter 6 h before the first postoperative dose'),
        restart: plain('6 h after catheter removal'),
        labs: plain('Fondaparinux-calibrated anti-Xa 0.1 IU/mL or less if placing the needle earlier than these times'),
        notes: plain('Treatment dosing. Renal impairment lengthens every interval.'),
        grade: 'IIC',
      },
    },
  },
  {
    id: 'dti',
    name: 'Argatroban and bivalirudin',
    aliases: ['argatroban', 'acova', 'bivalirudin', 'angiomax', 'parenteral dti', 'thrombin inhibitor'],
    cls: 'Anticoagulant, parenteral direct thrombin inhibitor',
    confirm: true,
    rec: {
      hold: danger('Against neuraxial techniques while on a parenteral thrombin inhibitor'),
      catheter: danger('No recommendation; neuraxial not advised. If unavoidable, involve hematology and document the discussion'),
      restart: plain('Not applicable'),
      labs: plain('aPTT tracks the effect, which is gone about 3 h after an IV dose'),
      notes: plain('Mostly used for HIT and PCI. No spinal hematomas reported, but almost no neuraxial experience either. Fourth-edition guidance; the fifth edition summary does not revisit it.'),
      grade: 'IIC',
    },
  },
  {
    id: 'thrombolytics',
    name: 'Thrombolytics',
    aliases: ['alteplase', 'tpa', 'tnk', 'tenecteplase', 'reteplase', 'streptokinase', 'urokinase', 'thrombolytic', 'fibrinolytic', 'lytic'],
    cls: 'Fibrinolytic',
    rec: {
      hold: danger('No needle placement for at least 48 h, and only after clotting studies including fibrinogen have normalised'),
      catheter: danger('Lytic given with a catheter in: neurological checks every 2 h for 48 h, an infusion that spares motor and sensory function, and fibrinogen to time removal'),
      restart: plain('Ask about any recent lumbar puncture, spinal, epidural or epidural steroid injection before giving a lytic'),
      labs: plain('Fibrinogen, among the last factors to recover, plus aPTT and PT/INR'),
      notes: plain('Lytics dissolve hemostatic plugs as well as clots. Avoid for 10 days after puncture of a non-compressible vessel.'),
      grade: 'IA',
    },
  },
  {
    id: 'herbal',
    name: 'Herbal supplements',
    aliases: ['garlic', 'ginkgo', 'ginseng', 'fish oil', 'omega 3', 'vitamin e', 'herbal', 'supplement', 'turmeric'],
    cls: 'Supplement',
    rec: {
      hold: safe('No mandatory hold'),
      catheter: safe('No restriction'),
      restart: safe('No restriction'),
      labs: plain('None'),
      notes: plain('Garlic, ginkgo and ginseng alter platelet function but have not been shown to raise spinal hematoma risk on their own. Caution when combined with an antiplatelet or anticoagulant and a catheter is planned.'),
      grade: 'IC',
    },
  },
];

/** Drugs whose name or aliases match a free-text query */
export const searchAsra = (query) => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const words = q.split(/\s+/).filter(Boolean);
  return ASRA_DRUGS.filter((d) =>
    [d.name.toLowerCase(), ...d.aliases].some((n) => words.some((w) => n.includes(w) || (w.length > 3 && w.includes(n)))));
};

export const asraDrug = (id) => ASRA_DRUGS.find((d) => d.id === id);
